import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {CellValue, ReportRow} from '../../data/reportRowModel';
import {CellRenderer} from './CellRenderer';
import {OVER_ESTIMATE_PATH, REPORTED_TIME_PATH} from './taskPaths';

const tokens = tokensForThemeProvider.sky.sys;

/** The figure and the warning on one line at their natural width, the way `CellRenderer` lays out a
 * status glyph beside its label. */
const ReportedTimeWithWarning = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

/** Keeps the glyph at its own size when the cell is narrower than the figure and the glyph together. */
const WarningGlyph = styled.span`
	display: inline-flex;
	flex-shrink: 0;
`;

/** The warning glyph. Its own component so `useIntl` runs only for a flagged row. The glyph has no
 * text of its own, so the wrapper carries the accessible name. */
const OverEstimateWarning = (): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<WarningGlyph role="img" aria-label={formatMessage({id: 'ai_report_builder.over_estimate.generic'})}>
			<Icon icon={'warning'} size={'m'} color={'error'} />
		</WarningGlyph>
	);
};

/** True for a task or subtask whose over-estimate flag is `true`. `false` is within estimate, and an
 * absent or `na` flag (no estimate) says nothing either way, so neither warns. */
function isOverEstimate(row: ReportRow): boolean {
	if (row.type !== 'task' && row.type !== 'subtask') {
		return false;
	}
	const flag = row.cells[OVER_ESTIMATE_PATH];
	return flag?.kind === 'boolean' && flag.value;
}

/** The task Reported time cell: the logged figure as any other duration cell renders it, plus a warning
 * icon to its right when the task's logged time is over its estimate.
 *
 * `value` is the Reported time cell as the column formats it; the flag is read off the row's own cells,
 * which the fetch requests alongside the Reported time column. A grouping header row has no cells, so
 * it never carries a flag. */
export function TaskReportedTimeCell({row, value}: {row: ReportRow; value: CellValue | undefined}): JSX.Element {
	const figure = <CellRenderer value={value} path={REPORTED_TIME_PATH} rowType={row.type} />;
	if (!isOverEstimate(row)) {
		return figure;
	}
	return (
		<ReportedTimeWithWarning>
			{figure}
			<OverEstimateWarning />
		</ReportedTimeWithWarning>
	);
}
